/**
 * InteractiveDemoBox — neutral container for in-page "try-this" widgets.
 *
 * Why this exists: every interactive that an explainer card embeds — a
 * stepper, a picker, an animated trace — wants the same visual shell so
 * the reader can tell at a glance "this is a thing I can poke." Rather
 * than rebuild the tinted, accented, bordered box inline at every
 * callsite, this component renders it once and accepts arbitrary children.
 *
 * The accent color tints both the background wash and the border, and is
 * the same theorem/structure color the host explainer uses elsewhere — so
 * the box visually anchors itself to its parent.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { withAlpha } from "../design-system/tokens.js";

export interface InteractiveDemoBoxProps {
  /** Accent color (theorem hex / structure-family hex) for tint and border. */
  readonly accent: string;
  /** Accessible label announced by screen readers. */
  readonly ariaLabel: string;
  /** Body content (controls, output, prose). */
  readonly children: React.ReactNode;
  /** Optional inline-style overrides applied to the outer box. */
  readonly style?: React.CSSProperties;
}

/**
 * Render an accent-tinted bordered container intended to host an in-page
 * interactive (stepper, picker, animated trace, etc.).
 *
 * The accent color is washed at ~5% opacity for the background and at
 * ~20% opacity for the border so the box is visually present but never
 * competes with the host card's chrome.
 */
export function InteractiveDemoBox({
  accent,
  ariaLabel,
  children,
  style,
}: InteractiveDemoBoxProps): React.JSX.Element {
  return (
    <div
      aria-label={ariaLabel}
      style={{
        marginTop: "0.75rem",
        padding: "0.875rem",
        backgroundColor: withAlpha(accent, "0D"),
        borderRadius: "0.5rem",
        border: `1px solid ${withAlpha(accent, "33")}`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
